import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import SupportSection from './components/SupportSection';
import Icon from '../../components/AppIcon';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Us - Cultaz | Sales & Support for Retail Management Software</title>
        <meta
          name="description"
          content="Get in touch with Cultaz for cloud-based or offline POS solutions. Contact our sales team for personalized consultation, support, and installation help. Nigerian retailers welcome."
        />
        <meta
          name="keywords"
          content="contact Cultaz, Cultaz support, retail software support, POS system support, Nigeria retail software contact, sales inquiry, customer support"
        />
        <meta name="author" content="Cultaz" />
        <meta property="og:title" content="Contact Cultaz - Sales & Support" />
        <meta property="og:description" content="Contact Cultaz for cloud or offline POS solutions. Sales, support, and installation help for Nigerian retailers. Reply within 24 hours." />
        <meta property="og:image" content="https://cultaz.com/banners/1.png" />
        <meta property="og:url" content="https://cultaz.com/support" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cultaz" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Cultaz - Sales & Support" />
        <meta name="twitter:description" content="Contact Cultaz for cloud or offline POS solutions. Sales, support, and installation help for Nigerian retailers." />
        <meta name="twitter:image" content="https://cultaz.com/banners/1.png" />
        <link rel="canonical" href="https://cultaz.com/support" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
            <div className="container mx-auto px-6 lg:px-12">
              <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
                <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4 md:mb-6">
                  <Icon name="MessageSquare" size={18} color="var(--color-primary)" strokeWidth={2} />
                  <span className="ml-2 font-body text-sm md:text-base font-medium text-primary">
                    Contact Sales
                  </span>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
                  Let's Discuss Your Retail Management Needs
                </h1>
                <p className="font-body text-base md:text-lg text-muted-foreground">
                  Whether you're interested in our cloud solution, desktop POS, or both, our team is ready to help you find the perfect fit for your business.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
                <div className="lg:col-span-2">
                  <ContactForm />
                </div>
                <div className="lg:col-span-1">
                  <ContactInfo />
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20">
            <div className="container mx-auto px-6 lg:px-12">
              <SupportSection />
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-primary to-secondary">
            <div className="container mx-auto px-6 lg:px-12">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
                  Ready to Transform Your Retail Business?
                </h2>
                <p className="font-body text-base md:text-lg text-white/90 mb-8 md:mb-10">
                  Join hundreds of Nigerian retailers who trust Cultaz for their daily operations. Start your free trial today or schedule a personalized demo.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://app.cultaz.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 rounded-lg bg-white text-primary font-body text-base md:text-lg font-semibold transition-smooth hover:bg-white/90 hover:scale-105 shadow-lg w-full sm:w-auto"
                  >
                    <Icon name="Rocket" size={20} color="currentColor" strokeWidth={2} className="mr-2" />
                    Start Free Trial
                  </a>
                  <a
                    href="/pricing"
                    className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 rounded-lg bg-white/10 text-white border-2 border-white/30 font-body text-base md:text-lg font-semibold transition-smooth hover:bg-white/20 hover:scale-105 w-full sm:w-auto"
                  >
                    View Pricing
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Contact;